import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _typeof from '@babel/runtime/helpers/typeof';
import '@babel/runtime/helpers/awaitAsyncGenerator';
import _wrapAsyncGenerator from '@babel/runtime/helpers/wrapAsyncGenerator';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import FastDeepEqual from 'fast-deep-equal';
import CloneDeep from 'clone-deep';

var REGEX_ALL_NON_FLOAT_CHARACTERS = /[^0-9.\-]/g;

/**
 * Returns true if the value is set (not undefined and not null).
 *
 * @param {*} value
 * @returns {boolean}
 */
var ISSET = function ISSET(value) {
  return typeof value !== 'undefined' && value !== null;
};

/**
 * Returns true if the value is an array.
 *
 * @param {*} value
 * @returns {boolean}
 */
var IS_ARRAY = function IS_ARRAY(value) {
  return Array.isArray(value);
};

/**
 * Ensures the given value is an array (returns the value wrapped in an array if it's not).
 *
 * @param {*} value
 * @returns {*[]}
 */
var ARRAY = function ARRAY(value) {
  return IS_ARRAY(value) ? value : typeof value !== 'undefined' ? [value] : [];
};

/**
 * Returns true if the value is an object.
 *
 * @param {*} value
 * @returns {boolean}
 */
var IS_OBJECT = function IS_OBJECT(value) {
  return _typeof(value) === 'object' && value !== null && !Array.isArray(value);
};

/**
 * Ensures the given value is an object (returns an empty object if it's not).
 *
 * @param value
 * @returns {object}
 */
var OBJECT = function OBJECT(value) {
  return IS_OBJECT(value) ? value : {};
};

/**
 * Ensures the given value is a string (casts it to a string if it's not, null and undefined will return an empty string).
 *
 * @param {*} value
 * @returns {string}
 */
var STRING = function STRING(value) {
  return ISSET(value) ? '' + value : '';
};

/**
 * Returns the first non-null non-undefined value as a string.
 *
 * @param {*} values
 * @returns {string}
 */
var STRING_ANY = function STRING_ANY() {
  for (var _len = arguments.length, values = new Array(_len), _key = 0; _key < _len; _key++) {
    values[_key] = arguments[_key];
  }
  for (var _i = 0, _values = values; _i < _values.length; _i++) {
    var value = _values[_i];
    if (ISSET(value)) {
      return '' + value;
    }
  }
  return '';
};

/**
 * Ensures the given value is an integer (attempts to cast it to an integer if it's not, null and undefined will return 0).
 *
 * @param {*} value
 * @returns {number}
 */
var INT = function INT(value) {
  return Math.round(FLOAT(value));
};

/**
 * Returns the first non-null non-undefined int-castable value as an integer.
 *
 * @param {*} values
 * @returns {number}
 */
var INT_ANY = function INT_ANY() {
  return Math.round(FLOAT_ANY.apply(void 0, arguments));
};

/**
 * Ensures the given value is a float (attempts to cast it to a float if it's not, null and undefined will return 0).
 *
 * @param {*} value
 * @returns {number}
 */
var FLOAT = function FLOAT(value) {
  var v = +value;
  if (!isNaN(v)) {
    return v;
  }
  return 0;
};

/**
 * Returns the first non-null non-undefined float-castable value as a float.
 *
 * @param {*} values
 * @returns {number}
 */
var FLOAT_ANY = function FLOAT_ANY() {
  for (var _len2 = arguments.length, values = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    values[_key2] = arguments[_key2];
  }
  for (var _i2 = 0, _values2 = values; _i2 < _values2.length; _i2++) {
    var value = _values2[_i2];
    if (value !== null) {
      var v = +value;
      if (!isNaN(v)) {
        return v;
      }
    }
  }
  return 0;
};

/**
 * Ensures the given value is an integer (attempts to cast it to an integer if it's not, null and undefined will return 0).
 * This version is less strict than INT, as it relies on parseFloat instead of on +value, meaning that it will accept strings that contain a number followed by other characters, which +value doesn't.
 *
 * @param {*} value
 * @returns {number}
 */
var INT_LAX = function INT_LAX(value) {
  return Math.round(FLOAT_LAX(value));
};

/**
 * Returns the first non-null non-undefined int-castable value as an integer.
 * This version is less strict than INT_ANY, as it relies on parseFloat instead of on +value, meaning that it will accept strings that contain a number followed by other characters, which +value doesn't.
 *
 * @param {*} values
 * @returns {number}
 */
var INT_LAX_ANY = function INT_LAX_ANY() {
  return Math.round(FLOAT_LAX_ANY.apply(void 0, arguments));
};

/**
 * Ensures the given value is a float (attempts to cast it to a float if it's not, null and undefined will return 0).
 * This version is less strict than FLOAT, as it relies on parseFloat instead of on +value, meaning that it will accept strings that contain a number followed by other characters, which +value doesn't.
 *
 * @param {*} value
 * @returns {number}
 */
var FLOAT_LAX = function FLOAT_LAX(value) {
  var v = typeof value === 'number' ? value : parseFloat((value + '').replace(REGEX_ALL_NON_FLOAT_CHARACTERS, ''));
  if (!isNaN(v)) {
    return v;
  }
  return 0;
};

/**
 * Returns the first non-null non-undefined float-castable value as a float.
 * This version is less strict than FLOAT_ANY, as it relies on parseFloat instead of on +value, meaning that it will accept strings that contain a number followed by other characters, which +value doesn't.
 *
 * @param {*} values
 * @returns {number}
 */
var FLOAT_LAX_ANY = function FLOAT_LAX_ANY() {
  for (var _len3 = arguments.length, values = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    values[_key3] = arguments[_key3];
  }
  for (var _i3 = 0, _values3 = values; _i3 < _values3.length; _i3++) {
    var value = _values3[_i3];
    if (value !== null) {
      var v = typeof value === 'number' ? value : parseFloat((value + '').replace(REGEX_ALL_NON_FLOAT_CHARACTERS, ''));
      if (!isNaN(v)) {
        return v;
      }
    }
  }
  return 0;
};

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

/**
 * @param {LeUtils~TransactionalValue} transactionalValue
 */
var checkTransactionalValue = function checkTransactionalValue(transactionalValue) {
  if (!LeUtils.isTransactionalValueValid(transactionalValue)) {
    console.error('The given value is not a valid TransactionalValue:');
    console.error(transactionalValue);
    throw new Error('The given value is not a valid TransactionalValue');
  }
};

/**
 * @param {LeUtils~TransactionalValue} transactionalValue
 * @param {string} changeId
 * @returns {{index:number, value:*}|null}
 */
var findTransactionalValueChange = function findTransactionalValueChange(transactionalValue, changeId) {
  for (var i = 0; i < transactionalValue.changes.length; i++) {
    var change = transactionalValue.changes[i];
    if (change.id === changeId) {
      return {
        index: i,
        value: change.value
      };
    }
  }
  return null;
};
var LeUtils = {
  /**
   * A deep equals implementation (npm package "fast-deep-equal").
   *
   * @param {*} value The value to compare.
   * @param {*} other The other value to compare.
   * @returns {boolean} Returns true if the values are equivalent.
   */
  equals: FastDeepEqual,
  /**
   * Returns a deep copy of the given value.
   *
   * @param {*} value
   * @returns {*}
   */
  clone: function clone(value) {
    return CloneDeep(value, true);
  },
  /**
   * Executes the given callback when the document is ready.
   *
   * @param {Function} callback
   * @returns {{remove:Function}}
   */
  onDomReady: function onDomReady(callback) {
    if (typeof window === 'undefined' || !document) {
      // no document, so we can't wait for it to be ready
      return {
        remove: function remove() {}
      };
    }
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      return LeUtils.setTimeout(callback, 0);
    } else {
      var listening = true;
      var _callbackWrapper = function callbackWrapper() {
        if (listening) {
          listening = false;
          document.removeEventListener('DOMContentLoaded', _callbackWrapper);
          callback();
        }
      };
      document.addEventListener('DOMContentLoaded', _callbackWrapper);
      return {
        remove: function remove() {
          if (listening) {
            listening = false;
            document.removeEventListener('DOMContentLoaded', _callbackWrapper);
          }
        }
      };
    }
  },
  /**
   * Parses the given version string, and returns an object with the major, minor, and patch numbers, as well as some comparison functions.
   *
   * Expects a version string such as:
   * - "1"
   * - "1.2"
   * - "1.2.3"
   * - "1.2.3 anything"
   * - "1.2.3-anything"
   *
   * @param {string|*} versionString
   * @returns {{major: (number),  minor: (number),  patch: (number),  toString: (function(): string),  equals: (function(string|*): boolean),  smallerThan: (function(string|*): boolean),  smallerThanOrEquals: (function(string|*): boolean),  largerThan: (function(string|*): boolean),  largerThanOrEquals: (function(string|*): boolean)}}
   */
  parseVersionString: function parseVersionString(versionString) {
    var _versionString, _versionString2, _versionString3;
    if (IS_OBJECT(versionString) && ISSET((_versionString = versionString) === null || _versionString === void 0 ? void 0 : _versionString.major) && ISSET((_versionString2 = versionString) === null || _versionString2 === void 0 ? void 0 : _versionString2.minor) && ISSET((_versionString3 = versionString) === null || _versionString3 === void 0 ? void 0 : _versionString3.patch)) {
      return versionString;
    }
    versionString = STRING(versionString).trim();
    var partsVersion = versionString.split(' ')[0].split('-')[0].split('.');
    var major = INT_LAX(partsVersion[0]);
    var minor = INT_LAX(partsVersion[1]);
    var patch = INT_LAX(partsVersion[2]);
    var THIS = {
      major: major,
      minor: minor,
      patch: patch,
      toString: function toString() {
        return major + '.' + minor + '.' + patch;
      },
      equals: function equals(otherVersion) {
        otherVersion = LeUtils.parseVersionString(otherVersion);
        return major === otherVersion.major && minor === otherVersion.minor && patch === otherVersion.patch;
      },
      largerThan: function largerThan(otherVersion) {
        otherVersion = LeUtils.parseVersionString(otherVersion);
        if (major > otherVersion.major) {
          return true;
        }
        if (major < otherVersion.major) {
          return false;
        }
        if (minor > otherVersion.minor) {
          return true;
        }
        if (minor < otherVersion.minor) {
          return false;
        }
        return patch > otherVersion.patch;
      },
      largerThanOrEquals: function largerThanOrEquals(otherVersion) {
        otherVersion = LeUtils.parseVersionString(otherVersion);
        if (major > otherVersion.major) {
          return true;
        }
        if (major < otherVersion.major) {
          return false;
        }
        if (minor > otherVersion.minor) {
          return true;
        }
        if (minor < otherVersion.minor) {
          return false;
        }
        return patch >= otherVersion.patch;
      },
      smallerThan: function smallerThan(otherVersion) {
        return !THIS.largerThanOrEquals(otherVersion);
      },
      smallerThanOrEquals: function smallerThanOrEquals(otherVersion) {
        return !THIS.largerThan(otherVersion);
      }
    };
    return THIS;
  },
  /**
   * Returns true if the array or object contains the given value.
   *
   * Values are compared by casting both of them to a string.
   *
   * @param {array|object|Function} array
   * @param {*} value
   * @returns {boolean}
   */
  contains: function contains(array, value) {
    if (!array) {
      return false;
    }
    var result = false;
    value = STRING(value);
    LeUtils.each(array, function (val) {
      if (STRING(val) === value) {
        result = true;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the array or object contains the given value.
   *
   * Values are compared by casting both of them to a string, and then lowercasing them.
   *
   * @param {array|object|Function} array
   * @param {*} value
   * @returns {boolean}
   */
  containsCaseInsensitive: function containsCaseInsensitive(array, value) {
    if (!array) {
      return false;
    }
    var result = false;
    value = STRING(value).toLowerCase();
    LeUtils.each(array, function (val) {
      if (STRING(val).toLowerCase() === value) {
        result = true;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the array or object contains all the given values.
   *
   * Values are compared by casting both of them to a string.
   *
   * @param {array|object|Function} array
   * @param {array|object|Function} values
   * @returns {boolean}
   */
  containsAll: function containsAll(array, values) {
    if (!array) {
      return false;
    }
    var result = true;
    LeUtils.each(values, function (value) {
      if (!LeUtils.contains(array, value)) {
        result = false;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the array or object contains all the given values.
   *
   * Values are compared by casting both of them to a string, and then lowercasing them.
   *
   * @param {array|object|Function} array
   * @param {array|object|Function} values
   * @returns {boolean}
   */
  containsAllCaseInsensitive: function containsAllCaseInsensitive(array, values) {
    if (!array) {
      return false;
    }
    var result = true;
    LeUtils.each(values, function (value) {
      if (!LeUtils.containsCaseInsensitive(array, value)) {
        result = false;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the array or object contains any of the given values.
   *
   * Values are compared by casting both of them to a string.
   *
   * @param {array|object|Function} array
   * @param {array|object|Function} values
   * @returns {boolean}
   */
  containsAny: function containsAny(array, values) {
    if (!array) {
      return false;
    }
    var result = false;
    LeUtils.each(values, function (value) {
      if (LeUtils.contains(array, value)) {
        result = true;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the array or object contains any of the given values.
   *
   * Values are compared by casting both of them to a string, and then lowercasing them.
   *
   * @param {array|object|Function} array
   * @param {array|object|Function} values
   * @returns {boolean}
   */
  containsAnyCaseInsensitive: function containsAnyCaseInsensitive(array, values) {
    if (!array) {
      return false;
    }
    var result = false;
    LeUtils.each(values, function (value) {
      if (LeUtils.containsCaseInsensitive(array, value)) {
        result = true;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the array or object contains none of the given values.
   *
   * Values are compared by casting both of them to a string.
   *
   * @param {array|object|Function} array
   * @param {array|object|Function} values
   * @returns {boolean}
   */
  containsNone: function containsNone(array, values) {
    if (!array) {
      return true;
    }
    var result = true;
    LeUtils.each(values, function (value) {
      if (LeUtils.contains(array, value)) {
        result = false;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the array or object contains none of the given values.
   *
   * Values are compared by casting both of them to a string, and then lowercasing them.
   *
   * @param {array|object|Function} array
   * @param {array|object|Function} values
   * @returns {boolean}
   */
  containsNoneCaseInsensitive: function containsNoneCaseInsensitive(array, values) {
    if (!array) {
      return true;
    }
    var result = true;
    LeUtils.each(values, function (value) {
      if (LeUtils.containsCaseInsensitive(array, value)) {
        result = false;
        return false;
      }
    });
    return result;
  },
  /**
   * @callback LeUtils~__findIndexValueCallback
   * @param {*} value
   * @param {*} index
   * @returns {boolean|undefined}
   */
  /**
   * Finds the first element in the given array or object that returns true from the callback, and returns an object with the index and value.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__findIndexValueCallback} callback
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {{index:*, value:*}|null}
   */
  findIndexValue: function findIndexValue(elements, callback) {
    var result = null;
    LeUtils.each(elements, function (value, index) {
      if (callback.call(elements[index], elements[index], index)) {
        result = {
          index: index,
          value: value
        };
        return false;
      }
    });
    return result;
  },
  /**
   * Finds the first element in the given array or object that returns true from the callback, and returns the index.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__findIndexValueCallback} callback
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*|null}
   */
  findIndex: function findIndex(elements, callback) {
    var _LeUtils$findIndexVal, _LeUtils$findIndexVal2;
    var optionalSkipHasOwnPropertyCheck = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    return (_LeUtils$findIndexVal = (_LeUtils$findIndexVal2 = LeUtils.findIndexValue(elements, callback, optionalSkipHasOwnPropertyCheck)) === null || _LeUtils$findIndexVal2 === void 0 ? void 0 : _LeUtils$findIndexVal2.index) !== null && _LeUtils$findIndexVal !== void 0 ? _LeUtils$findIndexVal : null;
  },
  /**
   * Finds the first element in the given array or object that returns true from the callback, and returns the value.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__findIndexValueCallback} callback
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*|null}
   */
  find: function find(elements, callback) {
    var _LeUtils$findIndexVal3, _LeUtils$findIndexVal4;
    var optionalSkipHasOwnPropertyCheck = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    return (_LeUtils$findIndexVal3 = (_LeUtils$findIndexVal4 = LeUtils.findIndexValue(elements, callback, optionalSkipHasOwnPropertyCheck)) === null || _LeUtils$findIndexVal4 === void 0 ? void 0 : _LeUtils$findIndexVal4.value) !== null && _LeUtils$findIndexVal3 !== void 0 ? _LeUtils$findIndexVal3 : null;
  },
  /**
   * @callback LeUtils~__eachCallback
   * @param {*} value
   * @param {*} index
   * @returns {boolean|undefined}
   */
  /**
   * Loops through each element in the given array or object, and calls the callback for each element.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__eachCallback} callback
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*[]|object|Function}
   */
  each: function each(elements, callback) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (elements !== null && typeof elements !== 'undefined') {
      if (Array.isArray(elements)) {
        for (var index = 0; index < elements.length; index++) {
          if (callback.call(elements[index], elements[index], index) === false) {
            break;
          }
        }
      } else if (_typeof(elements) === 'object' || typeof elements === 'function') {
        for (var _index in elements) {
          if (optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(elements, _index)) {
            if (callback.call(elements[_index], elements[_index], _index) === false) {
              break;
            }
          }
        }
      } else {
        console.warn('Executed LeUtils.each() on an invalid type: [' + _typeof(elements) + ']', elements);
      }
    }
    return elements;
  },
  /**
   * Like LeUtils.each(), except that it expects an async callback.
   *
   * @param {*[]|object|function} elements
   * @param {LeUtils~__eachCallback} asyncCallback
   * @param {number} [optionalParallelCount]
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*[]|object|function}
   */
  eachAsync: function () {
    var eachAsyncParallel = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(elements, asyncCallback, optionalParallelCount, optionalSkipHasOwnPropertyCheck) {
        var promises, doBreak;
        return _regeneratorRuntime.wrap(function _callee3$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              promises = [];
              doBreak = false;
              _context4.next = 4;
              return LeUtils.eachAsync(elements, /*#__PURE__*/function () {
                var _ref3 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(element, index) {
                  var _loop, promise;
                  return _regeneratorRuntime.wrap(function _callee2$(_context3) {
                    while (1) switch (_context3.prev = _context3.next) {
                      case 0:
                        _loop = /*#__PURE__*/_regeneratorRuntime.mark(function _loop() {
                          var newPromises;
                          return _regeneratorRuntime.wrap(function _loop$(_context2) {
                            while (1) switch (_context2.prev = _context2.next) {
                              case 0:
                                newPromises = [];
                                LeUtils.each(promises, function (promise) {
                                  if (!promise.__lowentry_utils__promise_is_done__) {
                                    newPromises.push(promise);
                                  }
                                });
                                promises = newPromises;
                                if (!(promises.length > optionalParallelCount)) {
                                  _context2.next = 6;
                                  break;
                                }
                                _context2.next = 6;
                                return Promise.any(promises);
                              case 6:
                              case "end":
                                return _context2.stop();
                            }
                          }, _loop);
                        });
                      case 1:
                        if (!(promises.length > optionalParallelCount)) {
                          _context3.next = 5;
                          break;
                        }
                        return _context3.delegateYield(_loop(), "t0", 3);
                      case 3:
                        _context3.next = 1;
                        break;
                      case 5:
                        if (!doBreak) {
                          _context3.next = 7;
                          break;
                        }
                        return _context3.abrupt("return", false);
                      case 7:
                        promise = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
                          return _regeneratorRuntime.wrap(function _callee$(_context) {
                            while (1) switch (_context.prev = _context.next) {
                              case 0:
                                _context.next = 2;
                                return asyncCallback.call(element, element, index);
                              case 2:
                                _context.t0 = _context.sent;
                                if (!(_context.t0 === false)) {
                                  _context.next = 5;
                                  break;
                                }
                                doBreak = true;
                              case 5:
                                promise.__lowentry_utils__promise_is_done__ = true;
                              case 6:
                              case "end":
                                return _context.stop();
                            }
                          }, _callee);
                        }))();
                        promises.push(promise);
                      case 9:
                      case "end":
                        return _context3.stop();
                    }
                  }, _callee2);
                }));
                return function (_x5, _x6) {
                  return _ref3.apply(this, arguments);
                };
              }(), optionalSkipHasOwnPropertyCheck);
            case 4:
              _context4.next = 6;
              return Promise.all(promises);
            case 6:
              return _context4.abrupt("return", elements);
            case 7:
            case "end":
              return _context4.stop();
          }
        }, _callee3);
      }));
      return function eachAsyncParallel(_x, _x2, _x3, _x4) {
        return _ref2.apply(this, arguments);
      };
    }();
    return /*#__PURE__*/function () {
      var _ref5 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4(elements, asyncCallback) {
        var parallelCount,
          optionalSkipHasOwnPropertyCheck,
          index,
          _index2,
          _args5 = arguments;
        return _regeneratorRuntime.wrap(function _callee4$(_context5) {
          while (1) switch (_context5.prev = _context5.next) {
            case 0:
              parallelCount = _args5.length > 2 && _args5[2] !== undefined ? _args5[2] : 1;
              optionalSkipHasOwnPropertyCheck = _args5.length > 3 && _args5[3] !== undefined ? _args5[3] : false;
              if (!(elements !== null && typeof elements !== 'undefined')) {
                _context5.next = 36;
                break;
              }
              parallelCount = INT_LAX(parallelCount);
              if (!(parallelCount > 1)) {
                _context5.next = 8;
                break;
              }
              _context5.next = 7;
              return eachAsyncParallel(elements, asyncCallback, parallelCount, optionalSkipHasOwnPropertyCheck);
            case 7:
              return _context5.abrupt("return", _context5.sent);
            case 8:
              if (!Array.isArray(elements)) {
                _context5.next = 21;
                break;
              }
              index = 0;
            case 10:
              if (!(index < elements.length)) {
                _context5.next = 19;
                break;
              }
              _context5.next = 13;
              return asyncCallback.call(elements[index], elements[index], index);
            case 13:
              _context5.t0 = _context5.sent;
              if (!(_context5.t0 === false)) {
                _context5.next = 16;
                break;
              }
              return _context5.abrupt("break", 19);
            case 16:
              index++;
              _context5.next = 10;
              break;
            case 19:
              _context5.next = 36;
              break;
            case 21:
              if (!(_typeof(elements) === 'object' || typeof elements === 'function')) {
                _context5.next = 35;
                break;
              }
              _context5.t1 = _regeneratorRuntime.keys(elements);
            case 23:
              if ((_context5.t2 = _context5.t1()).done) {
                _context5.next = 33;
                break;
              }
              _index2 = _context5.t2.value;
              if (!(optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(elements, _index2))) {
                _context5.next = 31;
                break;
              }
              _context5.next = 28;
              return asyncCallback.call(elements[_index2], elements[_index2], _index2);
            case 28:
              _context5.t3 = _context5.sent;
              if (!(_context5.t3 === false)) {
                _context5.next = 31;
                break;
              }
              return _context5.abrupt("break", 33);
            case 31:
              _context5.next = 23;
              break;
            case 33:
              _context5.next = 36;
              break;
            case 35:
              console.warn('Executed LeUtils.eachAsync() on an invalid type: [' + _typeof(elements) + ']', elements);
            case 36:
              return _context5.abrupt("return", elements);
            case 37:
            case "end":
              return _context5.stop();
          }
        }, _callee4);
      }));
      return function (_x7, _x8) {
        return _ref5.apply(this, arguments);
      };
    }();
  }(),
  /**
   * @callback LeUtils~__filterCallback
   * @param {*} value
   * @param {*} index
   * @returns {boolean|undefined}
   */
  /**
   * Loops through the given elements, and returns a new array or object, with only the elements that returned true (or a value equals to true) from the callback.
   * If no callback is given, it will return all elements that are of a true value (for example, values that are: not null, not undefined, not false, not 0, not an empty string, not an empty array, not an empty object).
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__filterCallback} [callback]
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*[]|object|Function}
   */
  filter: function filter(elements, callback) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (elements !== null && typeof elements !== 'undefined') {
      if (Array.isArray(elements)) {
        var result = [];
        for (var index = 0; index < elements.length; index++) {
          if (!callback && elements[index] || callback && callback.call(elements[index], elements[index], index)) {
            result.push(elements[index]);
          }
        }
        return result;
      } else if (_typeof(elements) === 'object' || typeof elements === 'function') {
        var _result = {};
        for (var _index3 in elements) {
          if (optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(elements, _index3)) {
            if (!callback && elements[_index3] || callback && callback.call(elements[_index3], elements[_index3], _index3)) {
              _result[_index3] = elements[_index3];
            }
          }
        }
        return _result;
      } else {
        console.warn('Executed LeUtils.filter() on an invalid type: [' + _typeof(elements) + ']', elements);
      }
    }
    return elements;
  },
  /**
   * @callback LeUtils~__mapCallback
   * @param {*} value
   * @param {*} index
   * @returns {*}
   */
  /**
   * Loops through the given elements, and returns a new array or object, with the elements that were returned from the callback.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__mapCallback} [callback]
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*[]|object|Function}
   */
  map: function map(elements, callback) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (elements !== null && typeof elements !== 'undefined') {
      if (Array.isArray(elements)) {
        var result = [];
        for (var index = 0; index < elements.length; index++) {
          if (!callback) {
            result[index] = elements[index];
          } else {
            result[index] = callback.call(elements[index], elements[index], index);
          }
        }
        return result;
      } else if (_typeof(elements) === 'object' || typeof elements === 'function') {
        var _result2 = {};
        for (var _index4 in elements) {
          if (optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(elements, _index4)) {
            if (!callback) {
              _result2[_index4] = elements[_index4];
            } else {
              _result2[_index4] = callback.call(elements[_index4], elements[_index4], _index4);
            }
          }
        }
        return _result2;
      } else {
        console.warn('Executed LeUtils.map() on an invalid type: [' + _typeof(elements) + ']', elements);
      }
    }
    return elements;
  },
  /**
   * @callback LeUtils~__mapToArrayCallback
   * @param {*} value
   * @param {*} index
   * @returns {*}
   */
  /**
   * Loops through the given elements, and returns a new array, with the elements that were returned from the callback. Always returns an array.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__mapToArrayCallback} [callback]
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*[]}
   */
  mapToArray: function mapToArray(elements, callback) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var result = [];
    if (elements !== null && typeof elements !== 'undefined') {
      if (Array.isArray(elements)) {
        for (var index = 0; index < elements.length; index++) {
          if (!callback) {
            result.push(elements[index]);
          } else {
            result.push(callback.call(elements[index], elements[index], index));
          }
        }
      } else if (_typeof(elements) === 'object' || typeof elements === 'function') {
        for (var _index5 in elements) {
          if (optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(elements, _index5)) {
            if (!callback) {
              result.push(elements[_index5]);
            } else {
              result.push(callback.call(elements[_index5], elements[_index5], _index5));
            }
          }
        }
      } else {
        console.warn('Executed LeUtils.mapToArray() on an invalid type: [' + _typeof(elements) + ']', elements);
      }
    }
    return result;
  },
  /**
   * @callback LeUtils~__mapToArraySortedCallback
   * @param {*} value
   * @param {*} index
   * @returns {*}
   */
  /**
   * Loops through the given elements, and returns a new array, with the elements that were returned from the callback. The elements will be sorted by the result from the given comparator. Always returns an array.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__sortKeysComparatorCallback} comparator
   * @param {LeUtils~__mapToArraySortedCallback} [callback]
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*[]}
   */
  mapToArraySorted: function mapToArraySorted(elements, comparator, callback) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    var keys = LeUtils.sortKeys(elements, comparator, optionalSkipHasOwnPropertyCheck);
    var result = [];
    for (var i = 0; i < keys.length; i++) {
      if (!callback) {
        result.push(elements[keys[i]]);
      } else {
        result.push(callback.call(elements[keys[i]], elements[keys[i]], keys[i]));
      }
    }
    return result;
  },
  /**
   * @callback LeUtils~__sortKeysComparatorCallback
   * @param {*} elementA
   * @param {*} elementB
   * @returns {number}
   */
  /**
   * Loops through the given elements, and returns a new array, with the keys from the given elements, sorted by the result from the given comparator. Always returns an array.
   *
   * @param {*[]|object|Function} elements
   * @param {LeUtils~__sortKeysComparatorCallback} comparator
   * @param {boolean} [optionalSkipHasOwnPropertyCheck]
   * @returns {*[]}
   */
  sortKeys: function sortKeys(elements, comparator) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var keys = [];
    if (elements !== null && typeof elements !== 'undefined') {
      if (Array.isArray(elements)) {
        for (var index = 0; index < elements.length; index++) {
          keys.push(index);
        }
      } else if (_typeof(elements) === 'object' || typeof elements === 'function') {
        for (var _index6 in elements) {
          if (optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(elements, _index6)) {
            keys.push(_index6);
          }
        }
      } else {
        console.warn('Executed LeUtils.sortKeys() on an invalid type: [' + _typeof(elements) + ']', elements);
      }
    }
    keys.sort(function (a, b) {
      return comparator(elements[a], elements[b]);
    });
    return keys;
  },
  /**
   * Turns the given value(s) into a 1 dimensional array.
   *
   * Does the same thing as Array.flat(Infinity).
   *
   * @param {*} array
   * @returns {*[]}
   */
  flattenArray: function () {
    var _flattenArrayRecursive = function flattenArrayRecursive(result, array) {
      if (!Array.isArray(array)) {
        result.push(array);
        return;
      }
      array.forEach(function (entry) {
        _flattenArrayRecursive(result, entry);
      });
    };
    return function (array) {
      if (!Array.isArray(array)) {
        return [array];
      }
      var result = [];
      array.forEach(function (entry) {
        _flattenArrayRecursive(result, entry);
      });
      return result;
    };
  }(),
  /**
   * Compares two values. Primarily used for sorting.
   *
   * @param {*} a
   * @param {*} b
   * @returns {number}
   */
  compare: function compare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
  },
  /**
   * Compares two numbers. Primarily used for sorting.
   *
   * @param {number} a
   * @param {number} b
   * @returns {number}
   */
  compareNumbers: function compareNumbers(a, b) {
    return a - b;
  },
  /**
   * Compares two numeric strings. Primarily used for sorting.
   *
   * @param {string|number} a
   * @param {string|number} b
   * @returns {number}
   */
  compareNumericStrings: function compareNumericStrings(a, b) {
    a = STRING(a).trim();
    b = STRING(b).trim();
    if (a.length === b.length) {
      return a < b ? -1 : a > b ? 1 : 0;
    }
    return a.length < b.length ? -1 : 1;
  },
  /**
   * Compares two strings generated by LeUtils.timestamp(). Primarily used for sorting.
   *
   * @param {string} a
   * @param {string} b
   * @returns {number}
   */
  compareTimestampStrings: function compareTimestampStrings(a, b) {
    a = LeUtils.base64ToHex(STRING(a).replaceAll('-', '+').replaceAll('_', '/'));
    b = LeUtils.base64ToHex(STRING(b).replaceAll('-', '+').replaceAll('_', '/'));
    return LeUtils.compare(a, b);
  },
  /**
   * Returns true if the given object is empty, false otherwise.
   *
   * @param {object} obj
   * @param [optionalSkipHasOwnPropertyCheck]
   * @returns {boolean}
   */
  isEmptyObject: function isEmptyObject(obj) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    for (var field in obj) {
      if (optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(obj, field)) {
        return false;
      }
    }
    return true;
  },
  /**
   * Returns the number of fields in the given object.
   *
   * @param {object} obj
   * @param [optionalSkipHasOwnPropertyCheck]
   * @returns {number}
   */
  getObjectFieldsCount: function getObjectFieldsCount(obj) {
    var optionalSkipHasOwnPropertyCheck = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var count = 0;
    for (var field in obj) {
      if (optionalSkipHasOwnPropertyCheck === true || Object.prototype.hasOwnProperty.call(obj, field)) {
        count++;
      }
    }
    return count;
  },
  /**
   * Returns true if the given function is a generator function (like: "function* (){}"), returns false otherwise.
   *
   * @param {Function} func
   * @returns {boolean}
   */
  isGeneratorFunction: function () {
    var GeneratorFunction = _regeneratorRuntime.mark(function _callee5() {
      return _regeneratorRuntime.wrap(function _callee5$(_context6) {
        while (1) switch (_context6.prev = _context6.next) {
          case 0:
          case "end":
            return _context6.stop();
        }
      }, _callee5);
    }).constructor;
    var AsyncGeneratorFunction = _wrapAsyncGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
      return _regeneratorRuntime.wrap(function _callee6$(_context7) {
        while (1) switch (_context7.prev = _context7.next) {
          case 0:
          case "end":
            return _context7.stop();
        }
      }, _callee6);
    })).constructor;
    var RegularFunction = function () {}.constructor;
    var PossibleGeneratorFunctionNames = Array.from(new Set(['GeneratorFunction', 'AsyncFunction', 'AsyncGeneratorFunction', GeneratorFunction.name, GeneratorFunction.displayName, AsyncGeneratorFunction.name, AsyncGeneratorFunction.displayName])).filter(function (element) {
      return element && element !== RegularFunction.name && element !== RegularFunction.displayName;
    });
    return function (func) {
      if (!func) {
        return false;
      }
      var constructor = func.constructor;
      if (!constructor) {
        return false;
      }
      return constructor.name && PossibleGeneratorFunctionNames.includes(constructor.name) || constructor.displayName && PossibleGeneratorFunctionNames.includes(constructor.displayName);
    };
  }(),
  /**
   * @callback LeUtils~__setTimeoutCallback
   * @param {number} deltaTime
   */
  /**
   * Executes the callback after the given number of milliseconds. Passes the elapsed time in seconds to the callback.
   *
   * To cancel the timeout, call remove() on the result of this function (example: "const timeoutHandler = LeUtils.setTimeout((deltaTime)=>{}, 1000); timeoutHandler.remove();")
   *
   * @param {LeUtils~__setTimeoutCallback} callback ([number] deltaTime)
   * @param {number} ms
   * @returns {{remove:Function}}
   */
  setTimeout: function (_setTimeout) {
    function setTimeout(_x9, _x10) {
      return _setTimeout.apply(this, arguments);
    }
    setTimeout.toString = function () {
      return _setTimeout.toString();
    };
    return setTimeout;
  }(function (callback, ms) {
    if (typeof window === 'undefined') {
      return {
        remove: function remove() {}
      };
    }
    ms = FLOAT_LAX(ms);
    var lastTime = performance.now();
    var handler = setTimeout(function () {
      var currentTime = performance.now();
      try {
        callback((currentTime - lastTime) / 1000);
      } catch (e) {
        console.error(e);
      }
      lastTime = currentTime;
    }, ms);
    return {
      remove: function remove() {
        if (handler !== null) {
          clearTimeout(handler);
          handler = null;
        }
      }
    };
  }),
  /**
   * @callback LeUtils~__setIntervalCallback
   * @param {number} deltaTime
   */
  /**
   * Executes the callback every given number of milliseconds. Passes the time difference in seconds between the last frame and now to it.
   *
   * To remove the interval, call remove() on the result of this function (example: "const intervalHandler = LeUtils.setInterval((deltaTime)=>{}, 1000); intervalHandler.remove();")
   *
   * @param {LeUtils~__setIntervalCallback} callback ([number] deltaTime)
   * @param {number} [intervalMs]
   * @param {boolean} [fireImmediately]
   * @returns {{remove:Function}}
   */
  setInterval: function (_setInterval) {
    function setInterval(_x11) {
      return _setInterval.apply(this, arguments);
    }
    setInterval.toString = function () {
      return _setInterval.toString();
    };
    return setInterval;
  }(function (callback) {
    var intervalMs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1000;
    var fireImmediately = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    intervalMs = FLOAT_LAX_ANY(intervalMs, 1000);
    if (fireImmediately) {
      try {
        callback(0);
      } catch (e) {
        console.error(e);
      }
    }
    if (typeof window === 'undefined') {
      return {
        remove: function remove() {}
      };
    }
    var lastTime = performance.now();
    var handler = setInterval(function () {
      var currentTime = performance.now();
      try {
        callback((currentTime - lastTime) / 1000);
      } catch (e) {
        console.error(e);
      }
      lastTime = currentTime;
    }, intervalMs);
    return {
      remove: function remove() {
        if (handler !== null) {
          clearInterval(handler);
          handler = null;
        }
      }
    };
  }),
  /**
   * @callback LeUtils~__setAnimationFrameTimeoutCallback
   * @param {number} deltaTime
   */
  /**
   * Executes the callback after the given number of frames. Passes the elapsed time in seconds to the callback.
   *
   * To cancel the timeout, call remove() on the result of this function (example: "const timeoutHandler = LeUtils.setAnimationFrameTimeout((deltaTime){}, 5); timeoutHandler.remove();")
   *
   * @param {LeUtils~__setAnimationFrameTimeoutCallback} callback ([number] deltaTime)
   * @param {number} [frames]
   * @returns {{remove:Function}}
   */
  setAnimationFrameTimeout: function setAnimationFrameTimeout(callback) {
    var frames = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    if (typeof window === 'undefined') {
      return {
        remove: function remove() {}
      };
    }
    frames = INT_LAX_ANY(frames, 1);
    var run = true;
    var requestAnimationFrameId = null;
    var lastTime = performance.now();
    var _tick = function tick() {
      if (run) {
        if (frames <= 0) {
          run = false;
          requestAnimationFrameId = null;
          var currentTime = performance.now();
          try {
            callback((currentTime - lastTime) / 1000);
          } catch (e) {
            console.error(e);
          }
          lastTime = currentTime;
          return;
        }
        frames--;
        requestAnimationFrameId = typeof window === 'undefined' ? setTimeout(_tick, 1000 / 60) : requestAnimationFrame(_tick);
      }
    };
    _tick();
    return {
      remove: function remove() {
        run = false;
        if (requestAnimationFrameId !== null) {
          typeof window === 'undefined' ? clearTimeout(requestAnimationFrameId) : cancelAnimationFrame(requestAnimationFrameId);
          requestAnimationFrameId = null;
        }
      }
    };
  },
  /**
   * @callback LeUtils~__setAnimationFrameIntervalCallback
   * @param {number} deltaTime
   */
  /**
   * Executes the callback every given number of frames. Passes the time difference in seconds between the last frame and now to it.
   *
   * To remove the interval, call remove() on the result of this function (example: "const intervalHandler = LeUtils.setAnimationFrameInterval((deltaTime)=>{}, 5); intervalHandler.remove();")
   *
   * @param {LeUtils~__setAnimationFrameIntervalCallback} callback ([number] deltaTime)
   * @param {number} [intervalFrames]
   * @param {boolean} [fireImmediately]
   * @returns {{remove:Function}}
   */
  setAnimationFrameInterval: function setAnimationFrameInterval(callback) {
    var intervalFrames = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    var fireImmediately = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    intervalFrames = INT_LAX_ANY(intervalFrames, 1);
    if (fireImmediately) {
      try {
        callback(0);
      } catch (e) {
        console.error(e);
      }
    }
    if (typeof window === 'undefined') {
      return {
        remove: function remove() {}
      };
    }
    var run = true;
    var requestAnimationFrameId = null;
    var lastTime = performance.now();
    var frames = intervalFrames;
    var _tick2 = function tick() {
      if (run) {
        if (frames <= 0) {
          var currentTime = performance.now();
          try {
            callback((currentTime - lastTime) / 1000);
          } catch (e) {
            console.error(e);
          }
          lastTime = currentTime;
          frames = intervalFrames;
        }
        frames--;
        if (run) {
          requestAnimationFrameId = typeof window === 'undefined' ? setTimeout(_tick2, 1000 / 60) : requestAnimationFrame(_tick2);
        }
      }
    };
    typeof window === 'undefined' ? setTimeout(_tick2, 1000 / 60) : requestAnimationFrame(_tick2);
    return {
      remove: function remove() {
        run = false;
        if (requestAnimationFrameId !== null) {
          typeof window === 'undefined' ? clearTimeout(requestAnimationFrameId) : cancelAnimationFrame(requestAnimationFrameId);
          requestAnimationFrameId = null;
        }
      }
    };
  },
  /**
   * Returns a promise, which will be resolved after the given number of milliseconds.
   *
   * @param {number} ms
   * @returns {Promise}
   */
  promiseTimeout: function promiseTimeout(ms) {
    ms = FLOAT_LAX(ms);
    if (ms <= 0) {
      return new Promise(function (resolve) {
        return resolve();
      });
    }
    return new Promise(function (resolve) {
      return setTimeout(resolve, ms);
    });
  },
  /**
   * Returns a promise, which will be resolved after the given number of frames.
   *
   * @param {number} frames
   * @returns {Promise}
   */
  promiseAnimationFrameTimeout: function promiseAnimationFrameTimeout(frames) {
    frames = INT_LAX(frames);
    if (frames <= 0) {
      return new Promise(function (resolve) {
        return resolve();
      });
    }
    return new Promise(function (resolve) {
      return LeUtils.setAnimationFrameTimeout(resolve, frames);
    });
  },
  /**
   * Allows you to do a fetch, with built-in retry and abort functionality.
   *
   * @param {string} url
   * @param {Object} [options]
   * @returns {{then:Function, catch:Function, finally:Function, remove:Function, isRemoved:Function}}
   */
  fetch: function (_fetch) {
    function fetch(_x12, _x13) {
      return _fetch.apply(this, arguments);
    }
    fetch.toString = function () {
      return _fetch.toString();
    };
    return fetch;
  }(function (url, options) {
    var currentRetries = 0;
    var retries = INT_LAX(options === null || options === void 0 ? void 0 : options.retries);
    var controllerAborted = false;
    var controller = null;
    if (typeof window !== 'undefined' && typeof window.AbortController !== 'undefined') {
      controller = new AbortController();
    }
    var promise = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
      var _attemptFetch;
      return _regeneratorRuntime.wrap(function _callee8$(_context9) {
        while (1) switch (_context9.prev = _context9.next) {
          case 0:
            _attemptFetch = /*#__PURE__*/function () {
              var _ref7 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
                var _controller;
                var _controller2, response, _controller3;
                return _regeneratorRuntime.wrap(function _callee7$(_context8) {
                  while (1) switch (_context8.prev = _context8.next) {
                    case 0:
                      if (!(controllerAborted || (_controller = controller) !== null && _controller !== void 0 && (_controller = _controller.signal) !== null && _controller !== void 0 && _controller.aborted)) {
                        _context8.next = 2;
                        break;
                      }
                      throw new Error('Aborted');
                    case 2:
                      _context8.prev = 2;
                      _context8.next = 5;
                      return fetch(url, _objectSpread(_objectSpread({
                        signal: (_controller2 = controller) === null || _controller2 === void 0 ? void 0 : _controller2.signal
                      }, options !== null && options !== void 0 ? options : {}), {}, {
                        retries: undefined,
                        delay: undefined
                      }));
                    case 5:
                      response = _context8.sent;
                      if (response.ok) {
                        _context8.next = 8;
                        break;
                      }
                      throw new Error('Network request failed: ' + response.status + ' ' + response.statusText);
                    case 8:
                      return _context8.abrupt("return", response);
                    case 11:
                      _context8.prev = 11;
                      _context8.t0 = _context8["catch"](2);
                      if (!(controllerAborted || (_controller3 = controller) !== null && _controller3 !== void 0 && (_controller3 = _controller3.signal) !== null && _controller3 !== void 0 && _controller3.aborted)) {
                        _context8.next = 15;
                        break;
                      }
                      throw new Error('Aborted');
                    case 15:
                      if (!(currentRetries >= retries)) {
                        _context8.next = 17;
                        break;
                      }
                      throw _context8.t0;
                    case 17:
                      currentRetries++;
                      _context8.next = 20;
                      return LeUtils.promiseTimeout(typeof (options === null || options === void 0 ? void 0 : options.delay) === 'function' ? INT_LAX_ANY(options === null || options === void 0 ? void 0 : options.delay(currentRetries), 500) : INT_LAX_ANY(options === null || options === void 0 ? void 0 : options.delay, 500));
                    case 20:
                      _context8.next = 22;
                      return _attemptFetch();
                    case 22:
                      return _context8.abrupt("return", _context8.sent);
                    case 23:
                    case "end":
                      return _context8.stop();
                  }
                }, _callee7, null, [[2, 11]]);
              }));
              return function attemptFetch() {
                return _ref7.apply(this, arguments);
              };
            }();
            _context9.next = 3;
            return _attemptFetch();
          case 3:
            return _context9.abrupt("return", _context9.sent);
          case 4:
          case "end":
            return _context9.stop();
        }
      }, _callee8);
    }))();
    var result = {};
    result.then = function () {
      var _promise;
      promise = (_promise = promise).then.apply(_promise, arguments);
      return result;
    };
    result["catch"] = function () {
      var _promise2;
      promise = (_promise2 = promise)["catch"].apply(_promise2, arguments);
      return result;
    };
    result["finally"] = function () {
      var _promise3;
      promise = (_promise3 = promise)["finally"].apply(_promise3, arguments);
      return result;
    };
    result.remove = function () {
      controllerAborted = true;
      if (controller) {
        var _controller4;
        (_controller4 = controller).abort.apply(_controller4, arguments);
      }
      return result;
    };
    result.isRemoved = function () {
      var _controller5;
      return controllerAborted || !!((_controller5 = controller) !== null && _controller5 !== void 0 && (_controller5 = _controller5.signal) !== null && _controller5 !== void 0 && _controller5.aborted);
    };
    return result;
  }),
  /**
   * Returns true if the user is on a smartphone device (mobile).
   * Will return false if the user is on a tablet or on a desktop.
   *
   * In short:
   * - Mobile: True
   * - Tablet: False
   * - Desktop: False
   *
   * @returns {boolean}
   */
  platformIsMobile: function platformIsMobile() {
    var _window$navigator, _window$navigator2;
    if (typeof window === 'undefined') {
      return false;
    }
    // noinspection JSDeprecatedSymbols, JSUnresolvedReference
    /** navigator.userAgentData.mobile doesn't return the correct value on some platforms, so this is a work-around, code from:  http://detectmobilebrowsers.com **/
    var a = STRING(((_window$navigator = window.navigator) === null || _window$navigator === void 0 ? void 0 : _window$navigator.userAgent) || ((_window$navigator2 = window.navigator) === null || _window$navigator2 === void 0 ? void 0 : _window$navigator2.vendor) || window.opera || '');
    var b = a.substring(0, 4);
    return !!(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series([46])0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br([ev])w|bumb|bw-([nu])|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do([cp])o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly([-_])|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-([mpt])|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c([- _agpst])|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac([ \-/])|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja([tv])a|jbro|jemu|jigs|kddi|keji|kgt([ /])|klon|kpt |kwc-|kyo([ck])|le(no|xi)|lg( g|\/([klu])|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t([- ov])|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30([02])|n50([025])|n7(0([01])|10)|ne(([cm])-|on|tf|wf|wg|wt)|nok([6i])|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan([adt])|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c([-01])|47|mc|nd|ri)|sgh-|shar|sie([-m])|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel([im])|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c([- ])|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(b));
  },
  /**
   * Returns true if the user has a cursor (mouse, touchpad, etc).
   * In this context, a cursor is defined as an input device that can hover over elements without necessarily interacting with them.
   *
   * @returns {boolean}
   */
  platformHasCursor: function platformHasCursor() {
    var _window$matchMedia;
    if (typeof window === 'undefined') {
      return true;
    }
    return !LeUtils.platformIsMobile() && !((_window$matchMedia = window.matchMedia('(any-hover: none)')) !== null && _window$matchMedia !== void 0 && _window$matchMedia.matches);
  },
  /**
   * Returns the given string, with the first character capitalized.
   *
   * @param {String} string
   * @returns {string}
   */
  capitalize: function capitalize(string) {
    string = STRING(string).trim();
    if (string.length <= 0) {
      return string;
    }
    return string.charAt(0).toUpperCase() + string.slice(1);
  },
  /**
   * Returns true if the given string ends with any of the given characters or words.
   *
   * @param {string} string
   * @param {string|string[]} endingCharsStringOrArray
   * @returns {boolean}
   */
  endsWithAny: function endsWithAny(string, endingCharsStringOrArray) {
    string = STRING(string);
    var endingCharsArray;
    if (Array.isArray(endingCharsStringOrArray)) {
      endingCharsArray = endingCharsStringOrArray;
    } else {
      endingCharsArray = STRING(endingCharsStringOrArray).split('');
    }
    var result = false;
    LeUtils.each(endingCharsArray, function (chars) {
      if (string.endsWith(STRING(chars))) {
        result = true;
        return false;
      }
    });
    return result;
  },
  /**
   * Returns true if the given string starts with any of the given characters or words.
   *
   * @param {string} string
   * @param {string|string[]} startingCharsStringOrArray
   * @returns {boolean}
   */
  startsWithAny: function startsWithAny(string, startingCharsStringOrArray) {
    string = STRING(string);
    var startingCharsArray;
    if (Array.isArray(startingCharsStringOrArray)) {
      startingCharsArray = startingCharsStringOrArray;
    } else {
      startingCharsArray = STRING(startingCharsStringOrArray).split('');
    }
    var result = false;
    LeUtils.each(startingCharsArray, function (chars) {
      if (string.startsWith(STRING(chars))) {
        result = true;
        return false;
      }
    });
    return result;
  },
  /**
   * Trims the end of the given string, by removing the given characters from it.
   *
   * @param {string} string
   * @param {string|string[]} trimCharsStringOrArray
   */
  trimEnd: function trimEnd(string, trimCharsStringOrArray) {
    string = STRING(string);
    var endingCharsArray;
    if (Array.isArray(trimCharsStringOrArray)) {
      endingCharsArray = trimCharsStringOrArray;
    } else {
      endingCharsArray = STRING(trimCharsStringOrArray).split('');
    }
    var trimChars = function trimChars(chars) {
      chars = STRING(chars);
      if (string.endsWith(chars)) {
        string = string.substring(0, string.length - chars.length);
        run = true;
      }
    };
    var run = true;
    while (run) {
      run = false;
      LeUtils.each(endingCharsArray, trimChars);
    }
    return string;
  },
  /**
   * Trims the start of the given string, by removing the given characters from it.
   *
   * @param {string} string
   * @param {string|string[]} trimCharsStringOrArray
   */
  trimStart: function trimStart(string, trimCharsStringOrArray) {
    string = STRING(string);
    var startingCharsArray;
    if (Array.isArray(trimCharsStringOrArray)) {
      startingCharsArray = trimCharsStringOrArray;
    } else {
      startingCharsArray = STRING(trimCharsStringOrArray).split('');
    }
    var trimChars = function trimChars(chars) {
      chars = STRING(chars);
      if (string.startsWith(chars)) {
        string = string.substring(chars.length);
        run = true;
      }
    };
    var run = true;
    while (run) {
      run = false;
      LeUtils.each(startingCharsArray, trimChars);
    }
    return string;
  },
  /**
   * Trims the start and end of the given string, by removing the given characters from it.
   *
   * @param {string} string
   * @param {string|string[]} trimCharsStringOrArray
   */
  trim: function trim(string, trimCharsStringOrArray) {
    return LeUtils.trimEnd(LeUtils.trimStart(string, trimCharsStringOrArray), trimCharsStringOrArray);
  },
  /**
   * Returns the given string, trims the start and end, and makes sure it ends with a valid sentence ending character (such as !?;.).
   *
   * @param {string} sentence
   * @returns {string}
   */
  purgeSentence: function purgeSentence(sentence) {
    sentence = LeUtils.trimEnd(STRING(sentence).trim(), '.: \r\n\t');
    sentence += LeUtils.endsWithAny(sentence, '!?;') ? '' : '.';
    return sentence;
  },
  /**
   * Attempts to obtain and return an error message from the given error, regardless of what is passed to this function.
   *
   * @param {*} error
   * @returns {string}
   */
  purgeErrorMessage: function purgeErrorMessage(error) {
    var _error;
    if ((_error = error) !== null && _error !== void 0 && _error.message) {
      error = error.message;
    }
    if (typeof error === 'string') {
      var errorParts = error.split('threw an error:');
      error = errorParts[errorParts.length - 1];
    } else {
      try {
        error = JSON.stringify(error);
      } catch (e) {
        error = 'An unknown error occurred';
      }
    }
    return error.trim();
  },
  /**
   * Generates all permutations of the given names.
   *
   * For example, if you pass "foo" and "bar", it will return:
   * - foobar
   * - fooBar
   * - FooBar
   * - foo-bar
   * - foo_bar
   *
   * @param {string} names
   * @returns {string[]}
   */
  generateNamePermutations: function generateNamePermutations() {
    for (var _len = arguments.length, names = new Array(_len), _key = 0; _key < _len; _key++) {
      names[_key] = arguments[_key];
    }
    names = LeUtils.flattenArray(names).map(function (name) {
      return STRING(name).trim().toLowerCase();
    }).filter(function (name) {
      return name.length > 0;
    });
    var results = [];
    if (names.length > 0) {
      results.push(names.join('')); //foobar
      results.push(names.map(LeUtils.capitalize).join('')); //FooBar
    }
    if (names.length > 1) {
      results.push([names[0]].concat(names.slice(1).map(LeUtils.capitalize)).join('')); //fooBar
      results.push(names.join('-')); //foo-bar
      results.push(names.join('_')); //foo_bar
    }
    return results;
  },
  /**
   * Increases the given numeric string by 1, this allows you to increase a numeric string without a limit.
   *
   * @param {string} string
   * @returns {string}
   */
  increaseNumericStringByOne: function increaseNumericStringByOne(string) {
    if (typeof string !== 'string') {
      string = '' + string;
      for (var i = string.length - 1; i >= 0; i--) {
        var c = string.charAt(i);
        if (c < '0' || c > '9') {
          return '1';
        }
      }
    }
    if (string === '') {
      return '1';
    }
    for (var _i = string.length - 1; _i >= 0; _i--) {
      var _c = string.charAt(_i);
      if (_c < '0' || _c > '9') {
        return '1';
      }
      if (_c < '9') {
        _c++;
        string = string.substring(0, _i) + _c + string.substring(_i + 1); // string[i] = (char + 1);
        break;
      }
      string = string.substring(0, _i) + '0' + string.substring(_i + 1); // string[i] = '0';
    }
    if (string.charAt(0) === '0') {
      string = '1' + string;
    }
    return string;
  },
  /**
   * Generates a base64 string (with +/ replaced by -_) that is guaranteed to be unique.
   *
   * @returns {string}
   */
  uniqueId: function () {
    var previousUniqueIdsTime = null;
    var previousUniqueIds = {};
    var numberToBytes = function numberToBytes(number) {
      var size = number === 0 ? 0 : Math.ceil((Math.floor(Math.log2(number)) + 1) / 8);
      var bytes = new Uint8ClampedArray(size);
      var x = number;
      for (var i = size - 1; i >= 0; i--) {
        var rightByte = x & 0xff;
        bytes[i] = rightByte;
        x = Math.floor(x / 0x100);
      }
      return bytes;
    };
    var generateUniqueId = function generateUniqueId() {
      var now;
      try {
        if (typeof window === 'undefined') {
          throw new Error();
        }
        // noinspection JSDeprecatedSymbols
        now = (performance.timeOrigin || performance.timing.navigationStart) + performance.now();
        if (typeof now !== 'number') {
          throw new Error();
        }
      } catch (e) {
        now = Date.now ? Date.now() : new Date().getTime();
      }
      now = Math.round(now);
      var nowBytes = numberToBytes(now);
      var uuid = null;
      try {
        var _crypto;
        uuid = (_crypto = crypto) === null || _crypto === void 0 ? void 0 : _crypto.randomUUID();
      } catch (e) {}
      if (uuid) {
        uuid = LeUtils.base64ToBytes(LeUtils.hexToBase64(uuid));
      } else {
        var bytesChunkA = numberToBytes((Math.random() + ' ').substring(2, 12).padEnd(10, '0'));
        var bytesChunkB = numberToBytes((Math.random() + ' ').substring(2, 12).padEnd(10, '0'));
        var bytesChunkC = numberToBytes((Math.random() + ' ').substring(2, 12).padEnd(10, '0'));
        var bytesChunkD = numberToBytes((Math.random() + ' ').substring(2, 12).padEnd(10, '0'));
        uuid = new Uint8Array(bytesChunkA.length + bytesChunkB.length + bytesChunkC.length + bytesChunkD.length);
        uuid.set(bytesChunkA, 0);
        uuid.set(bytesChunkB, bytesChunkA.length);
        uuid.set(bytesChunkC, bytesChunkA.length + bytesChunkB.length);
        uuid.set(bytesChunkD, bytesChunkA.length + bytesChunkB.length + bytesChunkC.length);
      }
      var bytes = new Uint8Array(nowBytes.length + uuid.length);
      bytes.set(nowBytes, 0);
      bytes.set(uuid, nowBytes.length);
      uuid = LeUtils.bytesToBase64(bytes).replaceAll('=', '').replaceAll('+', '-').replaceAll('/', '_');
      return {
        time: now,
        id: uuid
      };
    };
    return function () {
      while (true) {
        var result = generateUniqueId();
        if (previousUniqueIdsTime !== result.time) {
          previousUniqueIdsTime = result.time;
          previousUniqueIds = _defineProperty({}, result.id, true);
          return result.id;
        } else if (previousUniqueIds[result.id] !== true) {
          previousUniqueIds[result.id] = true;
          return result.id;
        }
      }
    };
  }(),
  /**
   * Generates a base64 string (with +/ replaced by -_) of the current time (in milliseconds since 1970).
   *
   * @param {number} [now] Optional time to use instead of the current time. If not set, the current time will be used.
   * @returns {string}
   */
  timestamp: function () {
    var numberToBytes = function numberToBytes(number) {
      var size = number === 0 ? 0 : Math.ceil((Math.floor(Math.log2(number)) + 1) / 8);
      var bytes = new Uint8ClampedArray(size);
      var x = number;
      for (var i = size - 1; i >= 0; i--) {
        var rightByte = x & 0xff;
        bytes[i] = rightByte;
        x = Math.floor(x / 0x100);
      }
      return bytes;
    };
    return function () {
      var now = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      if (ISSET(now)) {
        now = FLOAT_LAX(now);
      } else {
        try {
          if (typeof window === 'undefined') {
            throw new Error();
          }
          // noinspection JSDeprecatedSymbols
          now = (performance.timeOrigin || performance.timing.navigationStart) + performance.now();
          if (typeof now !== 'number') {
            throw new Error();
          }
        } catch (e) {
          now = Date.now ? Date.now() : new Date().getTime();
        }
      }
      now = Math.round(now);
      var nowBytes = numberToBytes(now);
      return LeUtils.bytesToBase64(nowBytes).replaceAll('=', '').replaceAll('+', '-').replaceAll('/', '_');
    };
  }(),
  /**
   * Returns a data URL of a 1x1 transparent pixel.
   *
   * @returns {string}
   */
  getEmptyImageSrc: function getEmptyImageSrc() {
    // noinspection SpellCheckingInspection
    return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
  },
  /**
   * Calculates and returns the percentage of the part and total ((part / total) * 100).
   *
   * @param {number|string} part
   * @param {number|string} total
   * @returns {number}
   */
  getPercentage: function getPercentage(part, total) {
    part = FLOAT_LAX(part);
    total = FLOAT_LAX(total);
    if (total <= 0) {
      return 100;
    }
    return Math.max(0, Math.min(100, part / total * 100));
  },
  /**
   * Returns the pixels of the given Image object.
   *
   * @param {HTMLImageElement} image
   * @returns {Uint8ClampedArray}
   */
  getImagePixels: function getImagePixels(image) {
    if (typeof window === 'undefined' || !document) {
      return new Uint8ClampedArray();
    }
    var canvas = document.createElement('canvas');
    document.body.appendChild(canvas);
    try {
      var ctx = canvas.getContext('2d');
      var width = Math.floor(image.width);
      var height = Math.floor(image.height);
      if (width <= 0 || height <= 0) {
        canvas.width = 1;
        canvas.height = 1;
      } else {
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      }
      return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    } finally {
      canvas.parentNode.removeChild(canvas);
    }
  },
  /**
   * Returns the data URL (mimetype "image/png") of a colored version of the given Image object.
   *
   * @param {HTMLImageElement} image
   * @param {string} color
   * @returns {string}
   */
  getColoredImage: function getColoredImage(image, color) {
    if (typeof window === 'undefined' || !document) {
      return LeUtils.getEmptyImageSrc();
    }
    var canvas = document.createElement('canvas');
    document.body.appendChild(canvas);
    try {
      var ctx = canvas.getContext('2d');
      var width = Math.floor(image.width);
      var height = Math.floor(image.height);
      if (width <= 0 || height <= 0) {
        canvas.width = 1;
        canvas.height = 1;
      } else {
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      }
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      canvas.parentNode.removeChild(canvas);
    }
  },
  /**
   * Returns the hex color of the given RGB(A).
   *
   * @param {number[]} rgb
   * @returns {string}
   */
  rgbToHex: function rgbToHex(rgb) {
    return '#' + rgb.map(function (x) {
      var hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  },
  /**
   * Returns the RGB(A) of the given hex color.
   *
   * @param {string} hexstring
   * @returns {number[]}
   */
  hexToRgb: function hexToRgb(hexstring) {
    hexstring = hexstring.replace(/[^0-9A-F]/gi, '');
    var hasAlpha = hexstring.length === 4 || hexstring.length === 8;
    while (hexstring.length < 6) {
      hexstring = hexstring.replace(/(.)/g, '$1$1');
    }
    var result = hexstring.match(/\w{2}/g).map(function (a) {
      return parseInt(a, 16);
    });
    return [result[0], result[1], result[2]].concat(_toConsumableArray(hasAlpha ? [result[3]] : []));
  },
  /**
   * Returns the HSL(A) of the given RGB(A).
   *
   * @param {number[]} rgb
   * @returns {number[]}
   */
  rgbToHsl: function rgbToHsl(rgb) {
    var r = rgb[0] / 255;
    var g = rgb[1] / 255;
    var b = rgb[2] / 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var h,
      s,
      l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }
    return [h, s, l].concat(_toConsumableArray(rgb.length >= 4 ? [rgb[3] / 255] : []));
  },
  /**
   * Returns the RGB(A) of the given HSL(A).
   *
   * @param {number[]} hsl
   * @returns {number[]}
   */
  hslToRgb: function () {
    var hue2rgb = function hue2rgb(p, q, t) {
      if (t < 0) {
        t += 1;
      }
      if (t > 1) {
        t -= 1;
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t;
      }
      if (t < 1 / 2) {
        return q;
      }
      if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
      }
      return p;
    };
    return function (hsl) {
      var h = hsl[0];
      var s = hsl[1];
      var l = hsl[2];
      var r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }
      return [r * 255, g * 255, b * 255].concat(_toConsumableArray(hsl.length >= 4 ? [hsl[3] * 255] : [])).map(function (c) {
        return Math.max(0, Math.min(255, Math.round(c)));
      });
    };
  }(),
  /**
   * Returns the LAB(A) of the given RGB(A).
   *
   * @param {number[]} rgb
   * @returns {number[]}
   */
  rgbToLab: function rgbToLab(rgb) {
    var r = rgb[0] / 255;
    var g = rgb[1] / 255;
    var b = rgb[2] / 255;
    r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
    g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
    b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
    var x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
    var y = r * 0.2126 + g * 0.7152 + b * 0.0722;
    var z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
    x = x > 0.008856 ? Math.pow(x, 1 / 3) : 7.787 * x + 16 / 116;
    y = y > 0.008856 ? Math.pow(y, 1 / 3) : 7.787 * y + 16 / 116;
    z = z > 0.008856 ? Math.pow(z, 1 / 3) : 7.787 * z + 16 / 116;
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)].concat(_toConsumableArray(rgb.length >= 4 ? [rgb[3] / 255] : []));
  },
  /**
   * Returns the difference (calculated with DeltaE) of the LAB values of the given RGB values.
   *
   * Returns a number:
   *
   * <pre>
   *   < 1.0    is not perceptible by human eyes
   *     1-2    is perceptible through close observation
   *     2-10   is perceptible at a glance
   *     11-49  is more similar than opposite
   *     100    is exactly the opposite color
   * </pre>
   *
   * @param {number[]} rgbA
   * @param {number[]} rgbB
   * @returns {number}
   */
  getDifferenceBetweenRgb: function getDifferenceBetweenRgb(rgbA, rgbB) {
    var labA = LeUtils.rgbToLab(rgbA);
    var labB = LeUtils.rgbToLab(rgbB);
    return LeUtils.getDifferenceBetweenLab(labA, labB);
  },
  /**
   * Returns the difference (calculated with DeltaE) of the given LAB values. Ignores the Alpha channel.
   *
   * Returns a number:
   *
   * <pre>
   *   < 1.0    is not perceptible by human eyes
   *     1-2    is perceptible through close observation
   *     2-10   is perceptible at a glance
   *     11-49  is more similar than opposite
   *     100    is exactly the opposite color
   * </pre>
   *
   * @param {number[]} labA
   * @param {number[]} labB
   * @returns {number}
   */
  getDifferenceBetweenLab: function getDifferenceBetweenLab(labA, labB) {
    var deltaL = labA[0] - labB[0];
    var deltaA = labA[1] - labB[1];
    var deltaB = labA[2] - labB[2];
    var c1 = Math.sqrt(labA[1] * labA[1] + labA[2] * labA[2]);
    var c2 = Math.sqrt(labB[1] * labB[1] + labB[2] * labB[2]);
    var deltaC = c1 - c2;
    var deltaH = deltaA * deltaA + deltaB * deltaB - deltaC * deltaC;
    deltaH = deltaH < 0 ? 0 : Math.sqrt(deltaH);
    var sc = 1.0 + 0.045 * c1;
    var sh = 1.0 + 0.015 * c1;
    var deltaLKlsl = deltaL / 1.0;
    var deltaCkcsc = deltaC / sc;
    var deltaHkhsh = deltaH / sh;
    var i = deltaLKlsl * deltaLKlsl + deltaCkcsc * deltaCkcsc + deltaHkhsh * deltaHkhsh;
    return i < 0 ? 0 : Math.sqrt(i);
  },
  /**
   * Returns the RGB(A) of the given RGB(A) values, based on the given percentage (0-100).
   * This allows you to define a gradient of colors to fade in between, rather than just having a start and an end color.
   *
   * Usage:
   *
   * <pre>
   * LeUtils.getRgbOfGradient({
   *   0:  [255, 0,   0],
   *   33: [255, 255, 0],
   *   66: [0,   255, 0],
   *   100:[0,   255, 255],
   * }, 45.1234);
   * </pre>
   *
   * @param {{[percentage]: number[]}} gradient
   * @param {number} percentage
   * @returns {number[]}
   */
  getRgbOfGradient: function getRgbOfGradient(gradient, percentage) {
    percentage = Math.max(0, Math.min(100, FLOAT_LAX(percentage)));
    var closest = null;
    LeUtils.each(gradient, function (color, percent) {
      percent = INT_LAX(percent);
      if (closest === null) {
        closest = [percent, Math.abs(percentage - percent)];
      } else {
        var difference = Math.abs(percentage - percent);
        if (difference < closest[1]) {
          closest = [percent, difference];
        }
      }
    });
    if (closest === null) {
      return null;
    }
    closest = closest[0];
    var higher = 99999;
    var lower = -99999;
    LeUtils.each(gradient, function (color, percent) {
      percent = INT_LAX(percent);
      if (percent < closest) {
        if (percent > lower) {
          lower = percent;
        }
      }
      if (percent > closest) {
        if (percent < higher) {
          higher = percent;
        }
      }
    });
    if (higher === 99999) {
      higher = null;
    }
    if (lower === -99999) {
      lower = null;
    }
    if (higher === null && lower === null || higher === lower) {
      return gradient[closest];
    } else if (higher !== null && lower !== null) {
      var higherDifference = Math.abs(higher - percentage);
      var lowerDifference = Math.abs(percentage - lower);
      if (higherDifference > lowerDifference) {
        higher = closest;
      } else {
        lower = closest;
      }
    } else if (lower === null) {
      lower = closest;
    } else {
      higher = closest;
    }
    if (lower > higher) {
      var tmp = higher;
      higher = lower;
      lower = tmp;
    }
    var total = higher - lower;
    var part = percentage - lower;
    return LeUtils.getRgbBetween(gradient[lower], gradient[higher], part / total * 100);
  },
  /**
   * Returns the RGB(A) between the two given RGB(A) values, based on the given percentage (0-100).
   *
   * @param {number[]} startRgb
   * @param {number[]} endRgb
   * @param {number} percentage
   * @returns {number[]}
   */
  getRgbBetween: function getRgbBetween(startRgb, endRgb, percentage) {
    percentage = FLOAT_LAX(percentage);
    var partEnd = Math.max(0, Math.min(1, percentage / 100.0));
    var partStart = 1 - partEnd;
    var length = Math.min(startRgb.length, endRgb.length);
    var result = [];
    for (var i = 0; i < length; i++) {
      result.push(Math.max(0, Math.min(255, Math.round(startRgb[i] * partStart + endRgb[i] * partEnd))));
    }
    return result;
  },
  /**
   * An implementation of the btoa function, which should work in all environments.
   *
   * @param {string} string
   * @returns {string}
   */
  btoa: function (_btoa) {
    function btoa(_x14) {
      return _btoa.apply(this, arguments);
    }
    btoa.toString = function () {
      return _btoa.toString();
    };
    return btoa;
  }(function (string) {
    if (typeof btoa === 'function') {
      return btoa(string);
    }
    return Buffer.from(string).toString('base64');
  }),
  /**
   * An implementation of the atob function, which should work in all environments.
   *
   * @param {string} base64string
   * @returns {string}
   */
  atob: function (_atob) {
    function atob(_x15) {
      return _atob.apply(this, arguments);
    }
    atob.toString = function () {
      return _atob.toString();
    };
    return atob;
  }(function (base64string) {
    if (typeof atob === 'function') {
      return atob(base64string);
    }
    return Buffer.from(base64string, 'base64').toString();
  }),
  /**
   * Encodes a UTF-8 string into a base64 string.
   *
   * @param {string} string
   * @returns {string}
   */
  utf8ToBase64: function utf8ToBase64(string) {
    return LeUtils.btoa(encodeURIComponent(string).replace(/%([0-9A-F]{2})/g, function (match, p1) {
      return String.fromCharCode(parseInt(p1, 16));
    }));
  },
  /**
   * Decodes a base64 string back into a UTF-8 string.
   *
   * @param {string} base64string
   * @returns {string}
   */
  base64ToUtf8: function base64ToUtf8(base64string) {
    return decodeURIComponent(LeUtils.atob(base64string.trim()).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  },
  /**
   * Converts a base64 string into a hex string.
   *
   * @param {string} base64string
   * @returns {string}
   */
  base64ToHex: function base64ToHex(base64string) {
    return LeUtils.atob(base64string.trim()).split('').map(function (c) {
      return ('0' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join('');
  },
  /**
   * Converts a hex string into a base64 string.
   *
   * @param {string} hexstring
   * @returns {string}
   */
  hexToBase64: function hexToBase64(hexstring) {
    return LeUtils.btoa(hexstring.replace(/[^0-9A-F]/gi, '').match(/\w{2}/g).map(function (a) {
      return String.fromCharCode(parseInt(a, 16));
    }).join(''));
  },
  /**
   * Converts a base64 string into bytes (Uint8Array).
   *
   * @param {string} base64string
   * @returns {Uint8Array}
   */
  base64ToBytes: function base64ToBytes(base64string) {
    var binary = LeUtils.atob(base64string.trim());
    var len = binary.length;
    var data = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
      data[i] = binary.charCodeAt(i);
    }
    return data;
  },
  /**
   * Converts bytes into a base64 string.
   *
   * @param {ArrayLike<number>|ArrayBufferLike} arraybuffer
   * @returns {string}
   */
  bytesToBase64: function bytesToBase64(arraybuffer) {
    var bytes = new Uint8Array(arraybuffer);
    var len = bytes.byteLength;
    var binary = '';
    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return LeUtils.btoa(binary);
  },
  /**
   * Downloads the given base64 string as a file.
   *
   * @param {string} base64string
   * @param {string} [fileName]
   * @param {string} [mimeType]
   */
  downloadFile: function downloadFile(base64string, fileName, mimeType) {
    if (typeof window === 'undefined' || !document) {
      return;
    }
    var link = document.createElement('a');
    link.setAttribute('download', typeof fileName === 'string' ? fileName : 'file');
    link.href = 'data:' + mimeType + ';base64,' + base64string;
    link.setAttribute('target', '_blank');
    link.click();
  },
  /**
   * Loads the value from the browser, returns undefined if the value doesn't exist.
   *
   * @param {string} id
   * @returns {*}
   */
  localStorageGet: function localStorageGet(id) {
    if (typeof window === 'undefined') {
      return;
    }
    var result = window.localStorage.getItem('LeUtils_' + id);
    if (typeof result !== 'string') {
      return;
    }
    try {
      result = JSON.parse(result);
      if (typeof result['-'] !== 'undefined') {
        return result['-'];
      }
    } catch (e) {}
  },
  /**
   * Saves the given data in the browser.
   *
   * @param {string} id
   * @param {*} data
   */
  localStorageSet: function localStorageSet(id, data) {
    if (typeof window === 'undefined') {
      return;
    }
    if (typeof data === 'undefined') {
      window.localStorage.removeItem('LeUtils_' + id);
      return;
    }
    window.localStorage.setItem('LeUtils_' + id, JSON.stringify({
      '-': data
    }));
  },
  /**
   * Removes the data from the browser.
   *
   * @param {string} id
   */
  localStorageRemove: function localStorageRemove(id) {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.removeItem('LeUtils_' + id);
  },
  /**
   * Returns whether the current hostname (window.location.hostname) is private (such as localhost, 192.168.1.1, etc).
   * This can be used to determine if the app is running in a development environment or not.
   *
   * Only "localhost" and IPv4 addresses are supported. IPv6 addresses will always return false.
   *
   * @returns {boolean}
   */
  isCurrentHostPrivate: function () {
    var lastHostname = null;
    var lastResult = false;
    return function () {
      if (typeof window === 'undefined') {
        return false; // server-side rendering, who knows to who it is being served to, assume it's public
      }
      var hostname = window.location.hostname;
      if (lastHostname === hostname) {
        return lastResult;
      }
      lastHostname = hostname;
      lastResult = LeUtils.isGivenHostPrivate(lastHostname);
      return lastResult;
    };
  }(),
  /**
   * Returns true if the given hostname is private (such as localhost, 192.168.1.1, etc).
   *
   * Only "localhost" and IPv4 addresses are supported. IPv6 addresses will always return false.
   *
   * @param {string} host
   * @returns {boolean}
   */
  isGivenHostPrivate: function isGivenHostPrivate(host) {
    host = STRING(host).trim().toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return true;
    }
    if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(host)) {
      return false;
    }
    var parts = host.split('.');
    return parts[0] === '10' ||
    // 10.0.0.0 - 10.255.255.255
    parts[0] === '172' && parseInt(parts[1], 10) >= 16 && parseInt(parts[1], 10) <= 31 ||
    // 172.16.0.0 - 172.31.255.255
    parts[0] === '192' && parts[1] === '168'; // 192.168.0.0 - 192.168.255.255
  },
  /**
   * Creates and returns a new TreeSet.
   * A TreeSet is a set of elements, sorted by a comparator.
   * Binary search is used to find elements, which makes it very fast to find elements.
   *
   * The comparator is also used to determine if two values are equal to each other.
   * This way, you can have values that aren't the same be treated as if they are. This can be used to deal with issues such as floating point errors for example.
   *
   * @param {*[]} elements
   * @param {Function} comparator
   * @returns {{getElements: (function(): *[]),  getComparator: (function(): Function),  size: (function(): number),  isEmpty: (function(): boolean),  contains: (function(*): boolean),  first: (function(): *|undefined),  last: (function(): *|undefined),  pollFirst: (function(): *|undefined),  pollLast: (function(): *|undefined),  add: function(*),  addAll: function(*[]|object),  getEqualValue: (function(*): (*)),  getEqualValueOrAdd: (function(*): (*))}}
   */
  createTreeSet: function createTreeSet(elements, comparator) {
    comparator = comparator || LeUtils.compare;
    elements = elements || [];
    elements.sort(comparator);

    /**
     * Performs a binary search on the elements, and returns the result.
     *
     * @param {*} value
     * @returns {{found: boolean,  index: number,  value: *|undefined}}
     */
    var binarySearch = function binarySearch(value) {
      var low = 0;
      var high = elements.length - 1;
      while (low <= high) {
        var mid = Math.floor((low + high) / 2);
        var midValue = elements[mid];
        var cmp = comparator(midValue, value);
        if (cmp < 0) {
          low = mid + 1;
        } else if (cmp > 0) {
          high = mid - 1;
        } else {
          return {
            found: true,
            index: mid,
            value: midValue
          };
        }
      }
      return {
        found: false,
        index: low,
        value: undefined
      };
    };
    var treeSet = {
      /**
       * Returns the elements of the set.
       *
       * @returns {*[]}
       */
      getElements: function getElements() {
        return elements;
      },
      /**
       * Returns the comparator of the set.
       *
       * @returns {Function}
       */
      getComparator: function getComparator() {
        return comparator;
      },
      /**
       * Returns the size of the set.
       *
       * @returns {number}
       */
      size: function size() {
        return elements.length;
      },
      /**
       * Returns true if the set is empty, false otherwise.
       *
       * @returns {boolean}
       */
      isEmpty: function isEmpty() {
        return elements.length <= 0;
      },
      /**
       * Returns true if the set contains a value that is equal to the given value, returns false otherwise.
       *
       * @param {*} value
       * @returns {boolean}
       */
      contains: function contains(value) {
        return binarySearch(value).found;
      },
      /**
       * Returns the first element of the set, or undefined if it is empty.
       *
       * @returns {*|undefined}
       */
      first: function first() {
        return elements.length > 0 ? elements[0] : undefined;
      },
      /**
       * Returns the last element of the set, or undefined if it is empty.
       *
       * @returns {*|undefined}
       */
      last: function last() {
        return elements.length > 0 ? elements[elements.length - 1] : undefined;
      },
      /**
       * Removes and returns the first element of the set, or undefined if it is empty.
       *
       * @returns {*|undefined}
       */
      pollFirst: function pollFirst() {
        return elements.length > 0 ? elements.splice(0, 1)[0] : undefined;
      },
      /**
       * Removes and returns the last element of the set, or undefined if it is empty.
       *
       * @returns {*|undefined}
       */
      pollLast: function pollLast() {
        return elements.length > 0 ? elements.splice(elements.length - 1, 1)[0] : undefined;
      },
      /**
       * Adds the given value to the set. Will only do so if no equal value already exists.
       *
       * @param {*} value
       */
      add: function add(value) {
        var result = binarySearch(value);
        if (result.found) {
          return;
        }
        elements.splice(result.index, 0, value);
      },
      /**
       * Adds all the given values to the set. Will only do so if no equal value already exists.
       *
       * @param {*[]|object} values
       */
      addAll: function addAll(values) {
        LeUtils.each(values, treeSet.add);
      },
      /**
       * Returns an equal value that's already in the tree set, or undefined if no equal values in it exist.
       *
       * @param {*} value
       * @returns {*|undefined}
       */
      getEqualValue: function getEqualValue(value) {
        var result = binarySearch(value);
        if (result.found) {
          return result.value;
        }
        return undefined;
      },
      /**
       * Returns an equal value that's already in the tree set. If no equal values in it exist, the given value will be added and returned.
       *
       * @param {*} value
       * @returns {*}
       */
      getEqualValueOrAdd: function getEqualValueOrAdd(value) {
        var result = binarySearch(value);
        if (result.found) {
          return result.value;
        }
        elements.splice(result.index, 0, value);
        return value;
      }
    };
    return treeSet;
  },
  /**
   * @typedef {Object} LeUtils~TransactionalValue
   * @property {*} value
   * @property {{id:string, value:*}[]} changes
   */
  /**
   * Creates and returns a new TransactionalValue object.
   * With a TransactionalValue, you can keep track of changes to a value, and commit or cancel them.
   *
   * Multiple uncommitted changes can be made at the same time, the last change will be the one that overwrites older changes.
   * If that change is cancelled, the previous change will be the one that overwrites older changes.
   * This allows you to make multiple unconfirmed changes, and confirm or cancel each of them individually at any time.
   *
   * @param {*} [value]
   * @returns {LeUtils~TransactionalValue}
   */
  createTransactionalValue: function createTransactionalValue(value) {
    if (typeof value === 'undefined') {
      value = null;
    }
    return {
      value: value,
      changes: []
    };
  },
  /**
   * Returns true if the given value is a valid TransactionalValue, returns false if it isn't.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @returns {boolean}
   */
  isTransactionalValueValid: function isTransactionalValueValid(transactionalValue) {
    return _typeof(transactionalValue) === 'object' && 'value' in transactionalValue && 'changes' in transactionalValue && Array.isArray(transactionalValue.changes);
  },
  /**
   * Returns true if the given value is a TransactionalValue, false otherwise.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @returns {string}
   */
  transactionalValueToString: function transactionalValueToString(transactionalValue) {
    if (!LeUtils.isTransactionalValueValid(transactionalValue)) {
      return transactionalValue + '';
    }
    if (transactionalValue.changes.length <= 0) {
      return '' + transactionalValue.value;
    }
    var valuesString = '' + transactionalValue.value;
    for (var i = 0; i < transactionalValue.changes.length; i++) {
      valuesString += ' -> ' + transactionalValue.changes[i].value;
    }
    return transactionalValue.changes[transactionalValue.changes.length - 1].value + ' (' + valuesString + ')';
  },
  /**
   * Sets the committed value of the given TransactionalValue to the given value. Clears out the previously uncommitted changes.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @param {*} value
   */
  transactionSetAndCommit: function transactionSetAndCommit(transactionalValue, value) {
    checkTransactionalValue(transactionalValue);
    if (typeof value === 'undefined') {
      value = null;
    }
    transactionalValue.value = value;
    transactionalValue.changes = [];
  },
  /**
   * Sets the value of the given TransactionalValue to the given value, without yet committing it, meaning it can be committed or cancelled later.
   * It returns the ID of the change, which can be used to commit or cancel the change later.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @param {*} value
   * @returns {string}
   */
  transactionSetWithoutCommitting: function transactionSetWithoutCommitting(transactionalValue, value) {
    checkTransactionalValue(transactionalValue);
    if (typeof value === 'undefined') {
      value = null;
    }
    var id = LeUtils.uniqueId();
    transactionalValue.changes.push({
      id: id,
      value: value
    });
    return id;
  },
  /**
   * Commits the change with the given ID, making it the new committed value.
   * Returns true if the change was found and committed, returns false if it was already overwritten by a newer committed change.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @param {string} changeId
   * @returns {boolean}
   */
  transactionCommitChange: function transactionCommitChange(transactionalValue, changeId) {
    checkTransactionalValue(transactionalValue);
    var change = findTransactionalValueChange(transactionalValue, changeId);
    if (change === null) {
      return false;
    }
    transactionalValue.value = change.value;
    transactionalValue.changes.splice(0, change.index + 1);
    return true;
  },
  /**
   * Cancels the change with the given ID, removing it from the uncommitted changes.
   * Returns true if the change was found and removed, returns false if it was already overwritten by a newer committed change.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @param {string} changeId
   * @returns {boolean}
   */
  transactionCancelChange: function transactionCancelChange(transactionalValue, changeId) {
    checkTransactionalValue(transactionalValue);
    var change = findTransactionalValueChange(transactionalValue, changeId);
    if (change === null) {
      return false;
    }
    transactionalValue.changes.splice(change.index, 1);
    return true;
  },
  /**
   * Returns true if the change was found, meaning it can still make a difference to the final committed value of this TransactionalValue.
   * Returns false if it was already overwritten by a newer committed change, meaning that this change can no longer make a difference to the final committed value of this TransactionalValue.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @param {string} changeId
   * @returns {boolean}
   */
  transactionIsChangeRelevant: function transactionIsChangeRelevant(transactionalValue, changeId) {
    checkTransactionalValue(transactionalValue);
    return findTransactionalValueChange(transactionalValue, changeId) !== null;
  },
  /**
   * Returns the committed value of the given TransactionalValue.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @returns {*}
   */
  transactionGetCommittedValue: function transactionGetCommittedValue(transactionalValue) {
    checkTransactionalValue(transactionalValue);
    return transactionalValue.value;
  },
  /**
   * Returns the value (including any uncommitted changes made to it) of the given TransactionalValue.
   *
   * @param {LeUtils~TransactionalValue} transactionalValue
   * @returns {*}
   */
  transactionGetValue: function transactionGetValue(transactionalValue) {
    checkTransactionalValue(transactionalValue);
    if (transactionalValue.changes.length <= 0) {
      return transactionalValue.value;
    }
    return transactionalValue.changes[transactionalValue.changes.length - 1].value;
  },
  /**
   * Creates a worker thread. Workers have to be stored at /workers/{workerName}.worker.js for this to work.
   *
   * Example of a worker file:
   *
   * ```js
   * onmessage = (message) =>
   * {
   *     postMessage({
   *         ...message.data,
   *         results: ['...some expensive calculation involving message.data...'],
   *     });
   * };
   * ```
   *
   * Usage:
   *
   * ```js
   * const {results} = await (async () =>
   * {
   *     try
   *     {
   *         return await LeUtils.sendWorkerMessage('my-worker', {someData:[1, 2, 3, 4, 5]});
   *     }
   *     catch(error)
   *     {
   *         console.error('MyWorker: ', error);
   *         return {results:[]};
   *     }
   * })();
   * ```
   *
   * or, if you want more control over the number of threads you have (the above example will only create 1 thread per worker):
   *
   * ```js
   * const myWorker1 = LeUtils.createWorkerThread('my-worker'); // creates a thread, you can create multiple worker threads of the same worker, to run multiple instances in parallel
   * const myWorker2 = LeUtils.createWorkerThread('my-worker'); // same worker, another thread
   * const {results} = await (async () =>
   * {
   *     try
   *     {
   *         return await myWorker1.sendMessage({someData:[1, 2, 3, 4, 5]});
   *     }
   *     catch(error)
   *     {
   *         console.error('MyWorker: ', error);
   *         return {results:[]};
   *     }
   * })();
   * ```
   *
   * @param {string} name
   * @returns {{worker: Worker,  sendMessage: function(Object, {timeout: number|undefined}|undefined): Promise<Object>}}
   */
  createWorkerThread: function createWorkerThread(name) {
    if (typeof window === 'undefined' || typeof Worker === 'undefined') {
      return {
        worker: null,
        sendMessage: new Promise(function (resolve, reject) {
          reject('Workers are not supported in this environment');
        })
      };
    }
    var worker = new Worker('/workers/' + name + '.worker.js');
    var listeners = {};
    var addListener = function addListener(id, callback) {
      listeners[id] = callback;
    };
    var removeListener = function removeListener(id) {
      delete listeners[id];
    };
    var sendMessage = function sendMessage(data, options) {
      return new Promise(function (resolve, reject) {
        var _options$timeout;
        var id = LeUtils.uniqueId();
        addListener(id, resolve);
        setTimeout(function () {
          removeListener(id);
          reject('timeout');
        }, (_options$timeout = options === null || options === void 0 ? void 0 : options.timeout) !== null && _options$timeout !== void 0 ? _options$timeout : 10000);
        worker.postMessage(_objectSpread({
          id: id
        }, data));
      });
    };
    worker.onerror = function (error) {
      console.error('Worker ' + name + ':', error);
    };
    worker.onmessage = function (message) {
      var data = message.data;
      if (data !== null && data !== void 0 && data.id) {
        var callback = listeners[data.id];
        if (callback) {
          removeListener(data.id);
          callback(data);
        }
      }
    };
    return {
      worker: worker,
      sendMessage: sendMessage
    };
  },
  /**
   * Sends a message to the given worker. Creates a worker thread for this worker if it doesn't exist yet.
   *
   * See {@link LeUtils#createWorkerThread} for more info on how to use workers.
   *
   * @param {string} workerName
   * @param {Object} data
   * @param {{timeout: number|undefined}} [options]
   * @returns {Promise<Object>}
   */
  sendWorkerMessage: function () {
    var workers = {};
    return function (workerName, data, options) {
      if (!workers[workerName]) {
        workers[workerName] = LeUtils.createWorkerThread(workerName);
      }
      return workers[workerName].sendMessage(data, options);
    };
  }(),
  /**
   * Purges the given email address, returning an empty string if it's invalid.
   *
   * @param {string} email
   * @returns {string}
   */
  purgeEmail: function purgeEmail(email) {
    email = STRING(email).trim().toLowerCase().replace(/\s/g, '');
    if (!email.includes('@') || !email.includes('.')) {
      return '';
    }
    return email;
  },
  /**
   * Returns true if the focus is effectively clear, meaning that the user is not typing in an input field.
   *
   * @returns {boolean}
   */
  isFocusClear: function () {
    if (typeof window === 'undefined' || !document) {
      return function () {
        return true;
      };
    }
    var inputTypes = ['text', 'search', 'email', 'number', 'password', 'tel', 'time', 'url', 'week', 'month', 'date', 'datetime-local'];
    return function () {
      var _document, _document2;
      return !(((_document = document) === null || _document === void 0 || (_document = _document.activeElement) === null || _document === void 0 || (_document = _document.tagName) === null || _document === void 0 ? void 0 : _document.toLowerCase()) === 'input' && inputTypes.includes((_document2 = document) === null || _document2 === void 0 || (_document2 = _document2.activeElement) === null || _document2 === void 0 || (_document2 = _document2.type) === null || _document2 === void 0 ? void 0 : _document2.toLowerCase()));
    };
  }(),
  /**
   * Returns the user's locale. Returns 'en-US' if it can't be determined.
   *
   * @returns {string}
   */
  getUserLocale: function () {
    var userLocale = null;
    return function () {
      if (userLocale === null) {
        userLocale = function (_navigator$languages, _navigator) {
          if (typeof window === 'undefined' || !navigator) {
            return 'en-US';
          }
          var locales = (_navigator$languages = (_navigator = navigator) === null || _navigator === void 0 ? void 0 : _navigator.languages) !== null && _navigator$languages !== void 0 ? _navigator$languages : [];
          if (!IS_ARRAY(locales) || locales.length <= 0) {
            return 'en-US';
          }
          locales = locales.filter(function (locale) {
            return typeof locale === 'string' && locale.includes('-') && locale.toLowerCase() !== 'en-us';
          });
          if (locales.length <= 0) {
            return 'en-US';
          }
          var localesNoEnglish = locales.filter(function (locale) {
            return !locale.toLowerCase().startsWith('en-');
          });
          if (localesNoEnglish.length <= 0) {
            return locales[0];
          }
          return localesNoEnglish[0];
        }();
      }
      return userLocale;
    };
  }(),
  /**
   * Returns the user's locale date format. Always returns YYYY MM DD, with the character in between depending on the user's locale. Returns 'YYYY/MM/DD' if the user's locale can't be determined.
   *
   * @returns {string}
   */
  getUserLocaleDateFormat: function () {
    var userLocaleDateFormat = null;
    return function () {
      if (userLocaleDateFormat === null) {
        userLocaleDateFormat = function () {
          var _char = '/';
          if (typeof window !== 'undefined' && typeof window.Intl !== 'undefined' && typeof window.Intl.DateTimeFormat !== 'undefined') {
            var formattedDate = new window.Intl.DateTimeFormat(LeUtils.getUserLocale()).format();
            if (formattedDate.includes('-')) {
              _char = '-';
            } else if (formattedDate.includes('. ')) {
              _char = '.';
            } else if (formattedDate.includes('.')) {
              _char = '.';
            }
          }
          return 'YYYY' + _char + 'MM' + _char + 'DD';
        }();
      }
      return userLocaleDateFormat;
    };
  }()
};

export { ARRAY, FLOAT, FLOAT_ANY, FLOAT_LAX, FLOAT_LAX_ANY, INT, INT_ANY, INT_LAX, INT_LAX_ANY, ISSET, IS_ARRAY, IS_OBJECT, LeUtils, OBJECT, STRING, STRING_ANY };
//# sourceMappingURL=index.js.map
